import { prop, isEmpty, path, find, any, identity, map } from 'ramda';
import { TrackJS } from 'trackjs';
import { CanonicalLoader } from '@wayfinder/vms-state';
import * as LliamUtilities from '@locuslabs/lliam-utilities';
import * as Gtag from '../gtag';
import * as Cognito from '../cognito';
import { BANNER_COLORS, BANNER_ICONS, BANNER_SUCCESS, LOGIN_ISSUES } from '../constants';
import * as Routing from './routing';
import appInsights from '../../utils/appInsights';

import def from './define-action';
import { defineAction } from './define-action';

const {
    NOT_AUTHORIZED_EXCEPTION,
    INVALID_USER,
    NEW_PASSWORDS_MUST_MATCH,
    NEW_PASSWORD_REQUIRED,
    CODE_MISMATCH_EXCEPTION,
    INVALID_PASSWORD_EXCEPTION,
    LIMIT_EXCEEDED_EXCEPTION,
    INVALID_PARAMETER_EXCEPTION,
} = LOGIN_ISSUES;
const { CHANGED_PASSWORD, PROFILE_UPDATED, EMAIL_VERIFIED } = BANNER_SUCCESS;

const INITIAL_STATE = {
    initialized: false,
    loggedIn: false,
    loginPending: false,
    userData: null,
    banner: { message: '', color: '', icon: '' },
    showForgotPassword: false,
    showForgotPasswordCode: false,
    showRegisterUser: false,
    policy: {},
    insightsConfigs: [],
    insightsReport: {},
};

const MESSAGES = {
    [NOT_AUTHORIZED_EXCEPTION]: 'The password and email do not match.',
    [INVALID_USER]: 'The password and email do not match.',
    [NEW_PASSWORDS_MUST_MATCH]: 'The new passwords do not match.',
    [CHANGED_PASSWORD]: 'Your password was updated successfully.',
    [PROFILE_UPDATED]: 'Your profile was updated successfully.',
    [EMAIL_VERIFIED]: 'Your email has been verified.',
    [CODE_MISMATCH_EXCEPTION]: 'Invalid verification code. Please try again.',
    [INVALID_PASSWORD_EXCEPTION]:
        'Your password must be at least 8 characters and have an uppercase letter, lowercase letter, number, and symbol (! @ # % &).',
    [LIMIT_EXCEEDED_EXCEPTION]: "You've exceeded the number of attempts. Please try again later.",
    [INVALID_PARAMETER_EXCEPTION]: 'Your password must be at least 8 characters.',
    NEW_PASSWORD_REQUIRED,
};

// =====================================================================================================================
// ActionConstants
// =====================================================================================================================

const ActionConstants = {
    BEGIN_LOGIN: 'BEGIN_LOGIN',
    CLEAR_BANNER: 'CLEAR_BANNER',
    CLEAR_LOGIN: 'CLEAR_LOGIN',
    INITIALIZATION_COMPLETE: 'INITIALIZATION_COMPLETE',
    LOGIN_FAILED: 'LOGIN_FAILED',
    LOGIN_SUCCESS: 'LOGIN_SUCCESS',
    LOGOUT: 'LOGOUT',
    MESSAGE_BANNER: 'MESSAGE_BANNER',
    REFRESH_TOKEN: 'REFRESH_TOKEN',
    SHOW_FORGOT_PASSWORD: 'SHOW_FORGOT_PASSWORD',
    SHOW_FORGOT_PASSWORD_CODE: 'SHOW_FORGOT_PASSWORD_CODE',
    SHOW_REGISTER_USER: 'SHOW_REGISTER_USER',
    UPDATE_USER: 'UPDATE_USER',
    SET_TRACKJS: 'SET_TRACKJS',
    SET_GA: 'SET_GA',
    SET_USER_POLICY: 'SET_USER_POLICY',
    RESET_APP: 'RESET_APP',
    SET_INSIGHTS_CONFIGS: 'SET_INSIGHTS_CONFIGS',
    SET_INSIGHTS_REPORT: 'SET_INSIGHTS_REPORT',
};

// =====================================================================================================================
// Actions
// =====================================================================================================================

const Actions = {
    beginLogin: def(ActionConstants.BEGIN_LOGIN),
    clearBanner: def(ActionConstants.CLEAR_BANNER),
    clearLogin: def(ActionConstants.CLEAR_LOGIN),
    initializationComplete: def(ActionConstants.INITIALIZATION_COMPLETE, 'loggedIn', 'userData'),
    loginFailed: def(ActionConstants.LOGIN_FAILED),
    loginSuccess: def(ActionConstants.LOGIN_SUCCESS),
    logout: def(ActionConstants.LOGOUT),
    messageBanner: def(ActionConstants.MESSAGE_BANNER, 'message', 'color', 'icon', 'code'),
    showMessageBanner: defineAction(ActionConstants.MESSAGE_BANNER),
    refreshToken: def(ActionConstants.REFRESH_TOKEN),
    showForgotPassword: def(ActionConstants.SHOW_FORGOT_PASSWORD),
    showForgotPasswordCode: def(ActionConstants.SHOW_FORGOT_PASSWORD_CODE),
    showRegisterUser: def(ActionConstants.SHOW_REGISTER_USER),
    updateUser: def(ActionConstants.UPDATE_USER, 'userData'),
    setTrackJS: def(ActionConstants.SET_TRACKJS),
    setGA: def(ActionConstants.SET_GA, 'userId'),
    setUserPolicy: def(ActionConstants.SET_USER_POLICY, 'policy'),
    resetApp: def(ActionConstants.RESET_APP),
    setInsightsConfigs: def(ActionConstants.SET_INSIGHTS_CONFIGS, 'config'),
    setInsightsReport: def(ActionConstants.SET_INSIGHTS_REPORT, 'reportConfigId'),
};

// =====================================================================================================================
// Thunks// =====================================================================================================================

/*
 * We got a session for the user one way or another
 * Grab the users policy so we can determine what permissions they have for mapnotes and objectstores
 * Policy: {
 *     assets      : [Permission],
 *     groups      : [Permission],
 *     mapnotes    : [Permission],
 *     objectstores: [Permission]
 * }
 * Permission: {
 *     name: { type: String },
 *     permission: {
 *         type: String,
 *         enum: ["R", "W", "RW", "ADMIN"] // the level of access the user has for a particular resource
 *     }
 * }
 * Impure: pulls from S3; initializes the loader
 * @sig _initializationSucceeded :: (DispatchFunction, Object) -> Promise ()
 */
const initializationSucceeded = async (dispatch, userData) => {
    CanonicalLoader.initializeLoader(
        userData['custom:AccountId'],
        Cognito.accessToken,
        TrackJS,
        import.meta.env.VITE_RMS_ENV,
    );

    const userId = prop('sub', userData);
    const userPolicy = await CanonicalLoader.getDerivedPolicyForToken(); // Grab the users policy so we can see what permissions they have

    // mark complete
    dispatch(Actions.setTrackJS());
    dispatch(Actions.setGA(userId));
    dispatch(Actions.setUserPolicy(userPolicy.policy));

    const hasPeopleDashboardsPermission = Selectors.hasAtLeastReadPermissionToResource(
        LliamUtilities.CONSTANTS.PEOPLE_INSIGHT_DASHBOARDS,
        { session: userPolicy },
    );

    if (hasPeopleDashboardsPermission) {
        try {
            const insightsAnalyticsConfig = await CanonicalLoader.getInsightsAnalyticsConfig();
            dispatch(Actions.setInsightsConfigs(insightsAnalyticsConfig));

            if (!isEmpty(insightsAnalyticsConfig)) {
                createTimeoutForInsightsRefresh(dispatch, insightsAnalyticsConfig);
                dispatch(Actions.setInsightsReport(insightsAnalyticsConfig[0].id));
            }
        } catch (error) {
            console.log(error);
        }
    }
    dispatch(Actions.initializationComplete(true, userData));
};

const attemptRehydrate = () => async dispatch => {
    try {
        const sessionInfo = await Cognito.rehydrateStoredSession();
        const { loggedIn, userData } = sessionInfo;

        if (loggedIn) {
            await initializationSucceeded(dispatch, userData);
            Routing.Actions.goToInitialRoute();
            return userData;
        }
        dispatch(Actions.initializationComplete(false, userData));
        dispatch(Routing.Actions.goToLogin());
    } catch (e) {
        console.error(e);
        dispatch(Actions.initializationComplete(false));
        dispatch(Routing.Actions.goToLogin());
    }
};

/*
 * Redux-thunk middleware dispatcher to try to register a user; redirects to / on success
 *
 * Impure: writes Cognito data; redirects browser
 * @sig completeRegistration :: ({ email: String, password: String, newPassword: String, firstName: String, lastName: String }) -> DispatchFunction -> Promise ()
 */
const completeRegistration =
    ({ email, password, newPassword, firstName, lastName }) =>
    async dispatch => {
        try {
            const result = await Cognito.completeSignup({ email, password, newPassword, firstName, lastName });
            if (result.success) {
                window.location.replace('/');
                dispatch(Actions.clearLogin());
                dispatch(Actions.loginSuccess());
                return;
            }
            dispatch(Actions.loginFailed());
            dispatch(Actions.messageBanner(result.message, BANNER_COLORS.RED, BANNER_ICONS.FAIL, result.code));
        } catch (e) {
            dispatch(Actions.loginFailed());
            dispatch(Actions.messageBanner(e.message, BANNER_COLORS.RED, BANNER_ICONS.FAIL));
        }
    };

/*
 * Change the users password
 * @sig :: changePassword :: (String, String) -> DispatchFunction -> Boolean
 */
const changePassword = (oldPassword, newPassword) => async dispatch => {
    const result = await Cognito.changePassword(oldPassword, newPassword);

    if (result === 'SUCCESS') return true;

    dispatch(Actions.messageBanner(result.message, BANNER_COLORS.RED, BANNER_ICONS.FAIL));
    return false;
};

/*
 * Send the user a verification code to reset their password
 * @sig :: forgotPasswordEmail :: String -> DispatchFunction -> String
 */
export const forgotPasswordEmail = email => Cognito.forgotPasswordEmail(email);

/*
 * Sets the new password for a user. Requires verification code
 * @sig :: forgotPasswordSubmit :: (String, String, String) -> DispatchFunction -> Boolean
 */
const forgotPasswordSubmit = (email, code, newPassword) => async dispatch => {
    try {
        await Cognito.forgotPasswordSubmit(email, code, newPassword);

        Gtag.recordResetPassword();

        dispatch(
            Actions.messageBanner(null, BANNER_COLORS.GREEN, BANNER_ICONS.SUCCESS, BANNER_SUCCESS.CHANGED_PASSWORD),
        );
        dispatch(Actions.clearLogin());
        return true;
    } catch (err) {
        console.error(err);

        dispatch(
            Actions.showMessageBanner({
                message: 'Could not reset password. Please check that code is correct or try again.',
                color: BANNER_COLORS.RED,
                icon: BANNER_ICONS.FAIL,
            }),
        );

        return false;
    }
};

const updateUserAttributes = (email, firstName, lastName) => async dispatch => {
    const result = await Cognito.updateAttributes(email, firstName, lastName);

    if (result === 'SUCCESS') {
        const userData = await Cognito.currentUserAttributes();
        dispatch(Actions.updateUser(userData));

        return true;
    }

    console.log('Error updating attributes', result.message);

    dispatch(Actions.messageBanner(result.message, BANNER_COLORS.RED, BANNER_ICONS.FAIL));
    return false;
};

const verifyUserEmail = code => async dispatch => {
    try {
        await Cognito.verifyEmailAttr(code);
        return true;
    } catch (err) {
        console.log(err);
        dispatch(Actions.messageBanner('NO CODE', BANNER_COLORS.RED, BANNER_ICONS.FAIL));
        return false;
    }
};

const logout = () => async (dispatch, getState) => {
    const userId = path(['userData', 'sub'], getState());
    await Cognito.signOutUser(userId);

    dispatch(Actions.logout());
};

const createTimeoutForInsightsRefresh = (dispatch, configs) => {
    const firstConfig = configs[0];
    const expiration = new Date(prop('expiration', firstConfig));
    const timeTillRefresh = expiration.getTime() - Date.now();
    setTimeout(() => dispatch(rehydrateInsightsConfiguration()), timeTillRefresh);
};

const rehydrateInsightsConfiguration = () => async dispatch => {
    try {
        const insightsAnalyticsConfig = await CanonicalLoader.getInsightsAnalyticsConfig();
        dispatch(Actions.setInsightsConfigs(insightsAnalyticsConfig));
        dispatch(Actions.setInsightsReport(insightsAnalyticsConfig[0].id));

        createTimeoutForInsightsRefresh(dispatch, insightsAnalyticsConfig);
    } catch (error) {
        console.log(error);
    }
};

const Thunks = {
    attemptRehydrate,
    changePassword,
    completeRegistration,
    forgotPasswordEmail,
    forgotPasswordSubmit,
    initializationSucceeded,
    updateUserAttributes,
    verifyUserEmail,
    logout,
    rehydrateInsightsConfiguration,
};

// =====================================================================================================================
// Reducer
// =====================================================================================================================

const reducer = (state = INITIAL_STATE, action) => {
    switch (action.type) {
        case ActionConstants.INITIALIZATION_COMPLETE:
            return handleInitializationComplete(state, action);
        case ActionConstants.BEGIN_LOGIN:
            return { ...state, loginPending: true };
        case ActionConstants.LOGIN_SUCCESS:
            return { ...state, loginPending: false };
        case ActionConstants.LOGIN_FAILED:
            return { ...state, loginPending: false };
        case ActionConstants.LOGOUT:
            return { ...state, loggedIn: false };
        case ActionConstants.MESSAGE_BANNER:
            return handleBanner(state, action);
        case ActionConstants.CLEAR_BANNER:
            return { ...state, banner: { message: '', color: '', icon: '' } };
        case ActionConstants.UPDATE_USER:
            return { ...state, userData: action.userData };
        case ActionConstants.SHOW_FORGOT_PASSWORD:
            return { ...state, showForgotPassword: true };
        case ActionConstants.SHOW_FORGOT_PASSWORD_CODE:
            return { ...state, showForgotPasswordCode: true };
        case ActionConstants.SHOW_REGISTER_USER:
            return { ...state, showRegisterUser: true };
        case ActionConstants.CLEAR_LOGIN:
            return { ...state, showForgotPassword: false, showForgotPasswordCode: false, showRegisterUser: false };
        case ActionConstants.SET_TRACKJS:
            return handleSetTrackJS(state, action);
        case ActionConstants.SET_GA:
            return handleSetGoogleAnalytics(state, action);
        case ActionConstants.SET_USER_POLICY:
            return { ...state, policy: action.policy };
        case ActionConstants.SET_INSIGHTS_CONFIGS:
            return { ...state, insightsConfigs: action.config };
        case ActionConstants.SET_INSIGHTS_REPORT:
            return handleSetInsightsReport(state, action);
        default:
            return state;
    }
};

const handleInitializationComplete = (state, action) => {
    if (import.meta.env.MODE === 'production' && action.userData) {
        // appInsights params must not contain whitespace or ,;=|
        const disallowed = /[,;=|]/g;
        const accountId = action.userData['custom:AccountId'].trim().replace(disallowed, '_');
        const userId = action.userData['sub'].trim().replace(disallowed, '_');
        appInsights.setAuthenticatedUserContext(userId, accountId, true);
        TrackJS.configure({ userId });
    }

    return {
        ...state,
        loggedIn: action.loggedIn,
        initialized: true,
        userData: action.userData,
    };
};

const handleBanner = (state, action) => {
    return {
        ...state,

        banner: {
            message: action.message || MESSAGES[action.code] || 'Unknown Error',
            color: action.color,
            icon: action.icon,
            isVisible: true,
        },
    };
};

const handleSetTrackJS = (state, action) => {
    if (!import.meta.env.DEV) {
        TrackJS.install({
            token: 'a15a776ef76c4b91bd40a8d34c2bf30a',
            application: 'locusvms',
            network: { error: false },
        });
    }

    return state;
};

const handleSetGoogleAnalytics = (state, action) => {
    if (!import.meta.env.VITE_SIGN_IN_URL.includes('localhost')) {
        Gtag.initialize(action.userId);
    }

    return state;
};

const handleSetInsightsReport = (state, action) => {
    const reportConfig = find(c => c.id === action.reportConfigId, state.insightsConfigs);
    return { ...state, insightsReport: reportConfig };
};

// =====================================================================================================================
// Selectors
// =====================================================================================================================

const isSessionInitialized = state => path(['session', 'initialized'], state);
const isLoggedIn = state => path(['session', 'loggedIn'], state);
const isLoginPending = state => path(['session', 'loginPending'], state);
const getUserData = state => path(['session', 'userData'], state);
const getMessageBanner = state => path(['session', 'banner'], state);
const getShowForgotPassword = state => path(['session', 'showForgotPassword'], state);
const getShowForgotPasswordCode = state => path(['session', 'showForgotPasswordCode'], state);
const getShowRegisterUser = state => path(['session', 'showRegisterUser'], state);
const getAccountId = state => path(['session', 'userData', 'custom:AccountId'], state);
const getEmail = state => path(['session', 'userData', 'email'], state);
const getInsightsConfigs = state => path(['session', 'insightsConfigs'], state);
const getInsightsConfig = state => path(['session', 'insightsReport'], state);

/*
 * Checks a user policy to see if they have a minimum permission level for the passed in resource
 * ie: ('testvmsgso', policyObject, 'objectstores', 'RW') would check the policy to see if the user has at least RW access to the testvmsgso objectstore resource
 *
 * Resource types: 'mapnotes', 'objectstores', 'assets', 'groups', 'groupMembership', 'internalTools', 'beacons'
 * @sig permissionIsAtLeast :: (VenueId, Policy, String, String) -> Boolean
 */
const permissionIsAtLeast = (venueId, policy = {}, resource, minimumPermission) => {
    const permission = LliamUtilities.getPermission(policy, resource, venueId);
    if (isEmpty(policy)) return false;
    return LliamUtilities.permissionIsAtLeast(permission, minimumPermission);
};

/*
 * Check if the user has at least read permission to the given venue / resource
 * @sig hasReadPermissionToResource :: (VenueId, String, State) -> Boolean
 */
const hasReadPermissionToResource = (venueId, resource, state) => {
    const policy = path(['session', 'policy'], state);
    if (isEmpty(policy)) return false;
    return permissionIsAtLeast(venueId, policy, resource, LliamUtilities.CONSTANTS.READ);
};

/*
 * Check if the user has at least write permission to the given venue / resource
 * @sig hasWritePermissionToResource :: (VenueId, String, State) -> Boolean
 */
const hasWritePermissionToResource = (venueId, resource, state) => {
    const policy = path(['session', 'policy'], state);
    if (isEmpty(policy)) return false;
    return permissionIsAtLeast(venueId, policy, resource, LliamUtilities.CONSTANTS.READWRITE);
};

/*
 * Check if the user has at least admin permission to the given venue / resource
 * @sig hasAdminPermissionToResource :: (VenueId, String, State) -> Boolean
 */
const hasAdminPermissionToResource = (venueId, resource, state) => {
    const policy = path(['session', 'policy'], state);
    if (isEmpty(policy)) return false;
    return permissionIsAtLeast(venueId, policy, resource, LliamUtilities.CONSTANTS.ADMIN);
};

/*
 * Check if the user has at owner read permission to the given venue / resource
 * @sig hasOwnerPermissionToResource :: (VenueId, String, State) -> Boolean
 */
const hasOwnerPermissionToResource = (venueId, resource, state) => {
    const policy = path(['session', 'policy'], state);
    if (isEmpty(policy)) return false;
    return permissionIsAtLeast(venueId, policy, resource, LliamUtilities.CONSTANTS.OWNER);
};

/*
 * Check if the user has at least read permission to ANY resource
 */
const hasAtLeastReadPermissionToResource = (resource, state) => {
    const policy = path(['session', 'policy'], state);
    if (isEmpty(policy)) return false;
    const permissions = prop(resource, policy);

    const readPermissions = map(p => {
        const venueId = p.name;
        const permission = LliamUtilities.getPermission(policy, resource, venueId);
        return LliamUtilities.permissionIsAtLeast(permission, LliamUtilities.CONSTANTS.READ);
    }, permissions);

    return any(identity, readPermissions);
};

const Selectors = {
    isSessionInitialized,
    isLoggedIn,
    isLoginPending,
    getUserData,
    getMessageBanner,
    getShowForgotPassword,
    getShowForgotPasswordCode,
    getShowRegisterUser,
    getAccountId,
    getEmail,
    hasReadPermissionToResource,
    hasWritePermissionToResource,
    hasAdminPermissionToResource,
    hasOwnerPermissionToResource,
    hasAtLeastReadPermissionToResource,
    getInsightsConfigs,
    getInsightsConfig,
};

export default reducer;

export { reducer, Selectors, Actions, Thunks };
